import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export type VoiceRecorderState =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'sending';

interface UseVoiceRecorderArgs {
  /** Upload the recorded blob. Reject with thrown error → user sees a toast. */
  onSend: (blob: Blob, filename: string, durationMs: number) => Promise<void>;
  /** Reject clips shorter than this many ms (WhatsApp ≈ 1s). */
  minDurationMs?: number;
  /** Auto-stop+send when the clip reaches this length. */
  maxDurationMs?: number;
}

const MIME_CANDIDATES: { mime: string; ext: string }[] = [
  { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
  { mime: 'audio/mp4;codecs=mp4a.40.2', ext: 'm4a' },
  { mime: 'audio/mp4', ext: 'm4a' },
  { mime: 'audio/webm;codecs=opus', ext: 'webm' },
  { mime: 'audio/webm', ext: 'webm' },
];

function pickFormat(): { mime: string; ext: string } | null {
  if (typeof MediaRecorder === 'undefined') {
    return null;
  }
  for (const c of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(c.mime)) {
      return c;
    }
  }
  return null;
}

/**
 * Click-to-toggle voice recorder.
 *
 * Lifecycle:
 *  idle → start() → requesting → recording
 *  recording → cancel() → idle           (discarded)
 *  recording → stopAndSend() → sending → onSend → idle
 *
 * Clips below `minDurationMs` are dropped — accidental taps don't spam the chat.
 */
export function useVoiceRecorder({
  onSend,
  minDurationMs = 1000,
  maxDurationMs = 5 * 60 * 1000,
}: UseVoiceRecorderArgs) {
  const [state, setState] = useState<VoiceRecorderState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const formatRef = useRef<{ mime: string; ext: string } | null>(null);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const onSendRef = useRef(onSend);

  useEffect(() => {
    onSendRef.current = onSend;
  }, [onSend]);

  const cleanupStream = useCallback(() => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    const stream = streamRef.current;
    if (stream) {
      for (const t of stream.getTracks()) {
        try {
          t.stop();
        } catch {
          /* noop */
        }
      }
    }
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  // Safety: clean up the stream if the component using this hook unmounts mid-recording.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      const mr = recorderRef.current;
      if (mr && mr.state !== 'inactive') {
        try {
          mr.stop();
        } catch {
          /* noop */
        }
      }
      cleanupStream();
    };
  }, [cleanupStream]);

  const start = useCallback(async () => {
    if (state !== 'idle') {
      return;
    }
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      toast.error('Запись звука не поддерживается этим браузером');
      return;
    }
    const fmt = pickFormat();
    if (!fmt) {
      toast.error('Браузер не поддерживает запись аудио');
      return;
    }
    setState('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setState('idle');
      const name = (e as Error)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        toast.error('Нет доступа к микрофону');
      } else {
        toast.error('Не удалось получить микрофон');
      }
      return;
    }
    streamRef.current = stream;
    formatRef.current = fmt;
    chunksRef.current = [];
    cancelledRef.current = false;

    const mr = new MediaRecorder(stream, { mimeType: fmt.mime });
    recorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mr.onstop = async () => {
      const durationMs = Date.now() - startedAtRef.current;
      const cancelled = cancelledRef.current;
      const tooShort = !cancelled && durationMs < minDurationMs;
      const chunks = chunksRef.current.slice();
      const fmtUsed = formatRef.current ?? fmt;
      cleanupStream();

      if (cancelled || tooShort) {
        setState('idle');
        setElapsedMs(0);
        if (tooShort) {
          toast('Слишком короткое сообщение', { duration: 1500 });
        }
        return;
      }

      const blob = new Blob(chunks, { type: fmtUsed.mime });
      const filename = `voice-${Date.now()}.${fmtUsed.ext}`;
      setState('sending');
      try {
        await onSendRef.current(blob, filename, durationMs);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[voice] upload failed', err);
        }
        toast.error('Не удалось отправить голосовое сообщение');
      } finally {
        setState('idle');
        setElapsedMs(0);
      }
    };

    try {
      mr.start(100);
    } catch (e) {
      cleanupStream();
      setState('idle');
      if (import.meta.env.DEV) {
        console.warn('[voice] MediaRecorder.start failed', e);
      }
      toast.error('Не удалось начать запись');
      return;
    }

    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setState('recording');
    tickerRef.current = setInterval(() => {
      const ms = Date.now() - startedAtRef.current;
      setElapsedMs(ms);
      if (ms >= maxDurationMs) {
        // Reached the cap → finalize and send.
        const cur = recorderRef.current;
        if (cur && cur.state !== 'inactive') {
          cancelledRef.current = false;
          try {
            cur.stop();
          } catch {
            /* noop */
          }
        }
      }
    }, 100);
  }, [state, cleanupStream, minDurationMs, maxDurationMs]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const mr = recorderRef.current;
    if (mr && mr.state !== 'inactive') {
      try {
        mr.stop();
      } catch {
        /* noop */
      }
    } else {
      cleanupStream();
      setState('idle');
      setElapsedMs(0);
    }
  }, [cleanupStream]);

  const stopAndSend = useCallback(() => {
    setState((s) => {
      if (s !== 'recording') {
        return s;
      }
      cancelledRef.current = false;
      const mr = recorderRef.current;
      if (mr && mr.state !== 'inactive') {
        try {
          mr.stop();
        } catch {
          /* noop */
        }
      }
      return s;
    });
  }, []);

  return {
    state,
    elapsedMs,
    start,
    cancel,
    stopAndSend,
  };
}

export type UseVoiceRecorder = ReturnType<typeof useVoiceRecorder>;

/** Format ms → "M:SS" / "MM:SS". */
export function formatRecordingTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
